
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Edit, FileText, Trash2 } from "lucide-react";
import { Tables } from "@/integrations/supabase/types";
import { extractOriginalFileName } from "@/utils/fileOperations";

type Activity = Tables<"activities">;

interface ActivityCardProps {
  activity: Activity & {
    activity_categories?: {
      id: string;
      name: string;
      color: string | null;
      icon: string | null;
    } | null;
  };
  onEdit: (activity: Activity) => void;
  onDelete: (activityId: string) => void;
}

export const ActivityCard = ({ activity, onEdit, onDelete }: ActivityCardProps) => {
  const categoryName = activity.activity_categories?.name || activity.type;
  const categoryColor = activity.activity_categories?.color || activity.color;

  // Helper function to determine file type and get display name
  const getFileInfo = (downloadUrl: string) => {
    if (!downloadUrl) return { type: 'No file', displayName: '' };
    
    if (downloadUrl.startsWith('/')) {
      // Storage file - extract original filename
      const storagePath = downloadUrl.substring(1);
      const originalFileName = extractOriginalFileName(storagePath);
      return {
        type: 'File uploaded',
        displayName: originalFileName || 'Uploaded file'
      };
    } else {
      // External URL
      return {
        type: 'External URL',
        displayName: 'External resource'
      };
    }
  };

  const fileInfo = activity.download_url ? getFileInfo(activity.download_url) : null;

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <h3 className="text-lg font-semibold">{activity.title}</h3>
            <p className="text-gray-600 mt-1">{activity.description}</p>
            <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
              <span className="flex items-center">
                Category: 
                <span 
                  className="ml-1 px-2 py-1 rounded-full text-xs text-white"
                  style={{ backgroundColor: categoryColor || '#6B7280' }}
                >
                  {categoryName}
                </span>
              </span>
              {activity.difficulty && <span>Difficulty: {activity.difficulty}</span>}
              {activity.age_group && <span>Age: {activity.age_group}</span>}
              {fileInfo && (
                <span className="flex items-center" title={fileInfo.displayName}>
                  <FileText className="h-4 w-4 mr-1" />
                  {fileInfo.type}
                </span>
              )}
              <span className={`px-2 py-1 rounded-full text-xs ${
                activity.status === 'published' ? 'bg-green-100 text-green-800' :
                activity.status === 'draft' ? 'bg-yellow-100 text-yellow-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {activity.status}
              </span>
            </div>
          </div>
          <div className="flex space-x-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onEdit(activity)}
            >
              <Edit className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDelete(activity.id)}
            >
              <Trash2 className="h-4 w-4 text-red-600" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
